import React, { useState, useEffect } from 'react';
import { 
  getLocations, 
  getEmployees,
  getTemplates, 
  getWeekShifts, 
  getWeekAvailability,
  createShift,
  createAssignment,
  deleteAssignment,
  updateAssignment,
  getAssignmentsByShift
} from '../api';

function Scheduling() {
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [selectedCell, setSelectedCell] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [timeInputs, setTimeInputs] = useState({ start_time: '', end_time: '' });
  const [employeeSearch, setEmployeeSearch] = useState('');
  // Get tenant_id from logged-in user
const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user.tenant_id || 1;

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  // Use local date components to avoid timezone issues
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const dayNum = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayNum}`;
}

  const getDaysOfWeek = () => {
  const daysArray = [];
  // Parse weekStart as local date to avoid timezone shift
  const [year, month, day] = weekStart.split('-').map(Number);
  const start = new Date(year, month - 1, day);
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    // Format as YYYY-MM-DD using local date components
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    daysArray.push({
      date: dateStr,
      dayName: days[i],
      dayNum: date.getDate(),
      dayIndex: i
    });
  }
  return daysArray;
};

  const fetchData = async () => {
    try {
      const [locRes, empRes, tempRes, shiftRes, availRes] = await Promise.all([
        getLocations(tenantId),
        getEmployees(tenantId),
        getTemplates(tenantId),
        getWeekShifts(tenantId, weekStart),
        getWeekAvailability(tenantId, weekStart)
      ]);
      
      setLocations(locRes.data.filter(l => l.active));
      setEmployees(empRes.data.filter(e => e.active));
      setTemplates(tempRes.data);
      setShifts(shiftRes.data);
      setAvailability(availRes.data);

      // Fetch assignments for all shifts
      const allAssignments = [];
      for (const shift of shiftRes.data) {
        try {
          const assignRes = await getAssignmentsByShift(shift.id);
          allAssignments.push(...assignRes.data.map(a => ({ ...a, shift_id: shift.id })));
        } catch (err) {
          console.error('Failed to load assignments for shift', shift.id);
        }
      }
      setAssignments(allAssignments);

    } catch (err) {
      console.error('Failed to load scheduling data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const getShiftForLocationDate = (locationId, date) => {
    return shifts.find(s => s.location_id === locationId && s.shift_date.split('T')[0] === date);
  };

  const getAssignmentsForShift = (shiftId) => {
    return assignments.filter(a => a.shift_id === shiftId);
  };

  const isEmployeeOff = (employeeId, date) => {
    return availability.some(a => 
      a.employee_id === employeeId && 
      a.exception_date.split('T')[0] === date
    );
  };

  const isEmployeeScheduledElsewhere = (employeeId, date, excludeLocationId) => {
    const dateShifts = shifts.filter(s => s.shift_date.split('T')[0] === date && s.location_id !== excludeLocationId);
    for (const shift of dateShifts) {
      const shiftAssignments = getAssignmentsForShift(shift.id);
      if (shiftAssignments.some(a => a.employee_id === employeeId)) {
        return true;
      }
    }
    return false;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  const handleApplyTemplate = async () => {
    if (!window.confirm('Apply template to this week? This will add employees from the template who are not already scheduled and not off.')) {
      return;
    }

    setLoading(true);
    const weekDays = getDaysOfWeek();

    try {
      for (const location of locations) {
        for (const day of weekDays) {
          // Get template for this location/day
          const dayTemplates = templates.filter(t => 
            t.location_id === location.id && 
            t.day_of_week === day.dayIndex
          );

          if (dayTemplates.length === 0) continue;

          // Get or create shift
          let shift = getShiftForLocationDate(location.id, day.date);
          if (!shift) {
            const shiftRes = await createShift({
              location_id: location.id,
              shift_date: day.date,
              target_headcount: dayTemplates.length
            });
            shift = shiftRes.data;
          }

          // Add employees from template (skip if off or already assigned)
          for (const template of dayTemplates) {
            // Skip if employee has time off
            if (isEmployeeOff(template.employee_id, day.date)) {
              console.log(`Skipping ${template.first_name} - has time off`);
              continue;
            }

            // Skip if already assigned to this shift
            const existingAssignments = getAssignmentsForShift(shift.id);
            if (existingAssignments.some(a => a.employee_id === template.employee_id)) {
              continue;
            }

            // Create assignment with times from template
            try {
              await createAssignment({
                shift_id: shift.id,
                employee_id: template.employee_id,
                scheduled_start: template.start_time || null,
                scheduled_end: template.end_time || null
              });
            } catch (err) {
              console.error('Failed to create assignment:', err);
            }
          }
        }
      }

      await fetchData();
      alert('Template applied successfully!');
    } catch (err) {
      console.error('Failed to apply template:', err);
      alert('Failed to apply template');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (locationId, date, dayIndex) => {
    setSelectedCell({ locationId, date, dayIndex });
    setModalOpen(true);
  };

  const handleToggleEmployee = async (employeeId) => {
    if (!selectedCell) return;

    const { locationId, date, dayIndex } = selectedCell;
    let shift = getShiftForLocationDate(locationId, date);

    // Check if employee is already assigned
    const existingAssignment = shift 
      ? assignments.find(a => a.shift_id === shift.id && a.employee_id === employeeId)
      : null;

    try {
      if (existingAssignment) {
        // Remove assignment
        await deleteAssignment(existingAssignment.id);
      } else {
        // Create shift if it doesn't exist
        if (!shift) {
          const shiftRes = await createShift({
            location_id: locationId,
            shift_date: date,
            target_headcount: 1
          });
          shift = shiftRes.data;
        }

        // Check if employee has template time for this slot
        const template = templates.find(t => 
          t.location_id === locationId && 
          t.employee_id === employeeId && 
          t.day_of_week === dayIndex
        );

        // Create assignment with template time if available
        await createAssignment({
          shift_id: shift.id,
          employee_id: employeeId,
          scheduled_start: template?.start_time || null,
          scheduled_end: template?.end_time || null
        });
      }

      await fetchData();
    } catch (err) {
      console.error('Failed to update assignment:', err);
      alert('Failed to update assignment');
    }
  };

  const openTimeModal = (assignment, emp) => {
    setSelectedAssignment({ ...assignment, emp });
    setTimeInputs({
      start_time: assignment.scheduled_start ? assignment.scheduled_start.slice(0, 5) : '',
      end_time: assignment.scheduled_end ? assignment.scheduled_end.slice(0, 5) : ''
    });
    setTimeModalOpen(true);
  };

  const closeTimeModal = () => {
    setTimeModalOpen(false);
    setSelectedAssignment(null);
    setTimeInputs({ start_time: '', end_time: '' });
  };

  const handleSaveTime = async () => {
    if (!selectedAssignment) return;

    try {
      await updateAssignment(selectedAssignment.id, {
        scheduled_start: timeInputs.start_time || null,
        scheduled_end: timeInputs.end_time || null
      });
      await fetchData();
      closeTimeModal();
    } catch (err) {
      console.error('Failed to update time:', err);
      alert('Failed to update time');
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedCell(null);
    setEmployeeSearch('');
  };

  const handlePreviousWeek = () => {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    setWeekStart(prev.toISOString().split('T')[0]);
  };

  const handleNextWeek = () => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    setWeekStart(next.toISOString().split('T')[0]);
  };

  const handleThisWeek = () => {
    setWeekStart(getMonday(new Date()));
  };

  const weekDays = getDaysOfWeek();

  if (loading) {
    return (
      <div style={{ padding: '48px' }}>
        <p className="text-gray-600">Loading schedule...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '48px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Weekly Schedule</h1>
          <p className="text-gray-600 mt-1">Manage employee assignments for the week</p>
        </div>
        <button
          onClick={handleApplyTemplate}
          style={{
            padding: '12px 24px',
            backgroundColor: '#10b981',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Apply Template
        </button>
      </div>

      {/* Week Navigation */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '16px',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={handlePreviousWeek}
          style={{
            padding: '8px 16px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← Previous
        </button>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span className="text-lg font-semibold text-gray-900">
  Week of {(() => {
    const [year, month, day] = weekStart.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  })()}
</span>
          <button
            onClick={handleThisWeek}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            This Week
          </button>
        </div>

        <button
          onClick={handleNextWeek}
          style={{
            padding: '8px 16px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Next →
        </button>
      </div>

      {/* Schedule Grid */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', width: '150px' }}>
                Location
              </th>
              {weekDays.map((day) => (
                <th key={day.date} style={{ padding: '16px', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>
                  <div>{day.dayName}</div>
                  <div style={{ fontSize: '12px', fontWeight: '400', color: '#6b7280' }}>{day.dayNum}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {locations.map((location) => (
              <tr key={location.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '16px', fontWeight: '500', color: '#374151' }}>
                  {location.name}
                </td>
                {weekDays.map((day) => {
                  const shift = getShiftForLocationDate(location.id, day.date);
                  const cellAssignments = shift ? getAssignmentsForShift(shift.id) : [];
                  
                  return (
                    <td 
                      key={day.date}
                      style={{ 
                        padding: '8px', 
                        verticalAlign: 'top',
                        backgroundColor: cellAssignments.length > 0 ? '#f0f9ff' : '#ffffff',
                        minHeight: '80px'
                      }}
                    >
                      {cellAssignments.length === 0 ? (
                        <div 
                          onClick={() => handleCellClick(location.id, day.date, day.dayIndex)}
                          style={{ 
                            color: '#9ca3af', 
                            fontSize: '20px', 
                            textAlign: 'center',
                            padding: '20px',
                            cursor: 'pointer'
                          }}
                        >
                          —
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {cellAssignments.map((assignment) => {
                            const emp = employees.find(e => e.id === assignment.employee_id);
                            if (!emp) return null;
                            return (
                              <div
                                key={assignment.id}
                                style={{
                                  backgroundColor: '#dbeafe',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start'
                                }}
                              >
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTimeModal(assignment, emp);
                                  }}
                                  style={{ cursor: 'pointer', flex: 1 }}
                                >
                                  <div style={{ fontWeight: '500', color: '#1e40af' }}>
                                    {emp.first_name} {emp.last_name?.charAt(0)}.
                                  </div>
                                  {assignment.scheduled_start && (
                                    <div style={{ fontSize: '10px', color: '#3b82f6' }}>
                                      {formatTime(assignment.scheduled_start)}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteAssignment(assignment.id).then(() => fetchData());
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#dc2626',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: '0 0 0 8px',
                                    lineHeight: '1'
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                          {/* Add button */}
                          <button
                            onClick={() => handleCellClick(location.id, day.date, day.dayIndex)}
                            style={{
                              marginTop: '4px',
                              padding: '4px 8px',
                              backgroundColor: 'transparent',
                              border: '1px dashed #93c5fd',
                              borderRadius: '4px',
                              color: '#3b82f6',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            + Add
                          </button>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Remove Employee Modal */}
      {modalOpen && selectedCell && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            width: '400px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 className="text-lg font-bold text-gray-900">
                {locations.find(l => l.id === selectedCell.locationId)?.name} - {days[selectedCell.dayIndex]}
              </h3>
              <button
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Tap employees to add or remove them
            </p>

            <input
              type="text"
              placeholder="Search employees..."
              value={employeeSearch}
              onChange={(e) => setEmployeeSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                marginBottom: '12px',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ 
              overflowY: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {employees
                .filter(emp => 
                  `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(employeeSearch.toLowerCase())
                )
                .map((emp) => {
                const shift = getShiftForLocationDate(selectedCell.locationId, selectedCell.date);
                const isAssigned = shift 
                  ? assignments.some(a => a.shift_id === shift.id && a.employee_id === emp.id)
                  : false;
                const isOff = isEmployeeOff(emp.id, selectedCell.date);
                const isElsewhere = isEmployeeScheduledElsewhere(emp.id, selectedCell.date, selectedCell.locationId);

                // Check if employee has template time for this slot
                const template = templates.find(t => 
                  t.location_id === selectedCell.locationId && 
                  t.employee_id === emp.id && 
                  t.day_of_week === selectedCell.dayIndex
                );

                return (
                  <button
                    key={emp.id}
                    onClick={() => !isOff && !isElsewhere && handleToggleEmployee(emp.id)}
                    disabled={isOff || isElsewhere}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      backgroundColor: isOff ? '#fef2f2' : isElsewhere ? '#f3f4f6' : isAssigned ? '#dbeafe' : '#f9fafb',
                      border: isAssigned ? '2px solid #3b82f6' : '2px solid transparent',
                      borderRadius: '8px',
                      cursor: isOff || isElsewhere ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'left',
                      opacity: isOff || isElsewhere ? 0.6 : 1
                    }}
                  >
                    <div>
                      <span style={{ 
                        fontWeight: '500',
                        color: isOff ? '#dc2626' : isAssigned ? '#1d4ed8' : '#374151'
                      }}>
                        {emp.first_name} {emp.last_name}
                      </span>
                      {template?.start_time && !isOff && !isElsewhere && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: '#059669' }}>
                          ({formatTime(template.start_time)})
                        </span>
                      )}
                      {isOff && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', color: '#dc2626' }}>
                          (Off)
                        </span>
                      )}
                      {isElsewhere && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6b7280' }}>
                          (Scheduled elsewhere)
                        </span>
                      )}
                    </div>
                    {isAssigned && (
                      <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>✓</span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={closeModal}
              style={{
                marginTop: '16px',
                width: '100%',
                padding: '12px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Time Edit Modal */}
      {timeModalOpen && selectedAssignment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            width: '320px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 className="text-lg font-bold text-gray-900">
                Edit Time
              </h3>
              <button
                onClick={closeTimeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {selectedAssignment.emp?.first_name} {selectedAssignment.emp?.last_name}
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Start Time
              </label>
              <select
                value={timeInputs.start_time}
                onChange={(e) => setTimeInputs({ ...timeInputs, start_time: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff'
                }}
              >
                <option value="">Select time</option>
                {Array.from({ length: 24 }, (_, hour) => (
                  <React.Fragment key={hour}>
                    <option value={`${hour.toString().padStart(2, '0')}:00`}>
                      {hour === 0 ? '12:00 AM' : hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`}
                    </option>
                    <option value={`${hour.toString().padStart(2, '0')}:30`}>
                      {hour === 0 ? '12:30 AM' : hour < 12 ? `${hour}:30 AM` : hour === 12 ? '12:30 PM' : `${hour - 12}:30 PM`}
                    </option>
                  </React.Fragment>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                End Time <span style={{ fontWeight: '400', color: '#9ca3af' }}>(optional)</span>
              </label>
              <select
                value={timeInputs.end_time}
                onChange={(e) => setTimeInputs({ ...timeInputs, end_time: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff'
                }}
              >
                <option value="">Select time</option>
                {Array.from({ length: 24 }, (_, hour) => (
                  <React.Fragment key={hour}>
                    <option value={`${hour.toString().padStart(2, '0')}:00`}>
                      {hour === 0 ? '12:00 AM' : hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`}
                    </option>
                    <option value={`${hour.toString().padStart(2, '0')}:30`}>
                      {hour === 0 ? '12:30 AM' : hour < 12 ? `${hour}:30 AM` : hour === 12 ? '12:30 PM' : `${hour - 12}:30 PM`}
                    </option>
                  </React.Fragment>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setTimeInputs({ start_time: '', end_time: '' })}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Clear
              </button>
              <button
                onClick={handleSaveTime}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{
        marginTop: '24px',
        backgroundColor: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '8px',
        padding: '16px'
      }}>
        <h3 className="font-semibold text-blue-800 mb-2">How to use</h3>
        <ul className="text-sm text-blue-700" style={{ paddingLeft: '20px' }}>
          <li style={{ marginBottom: '4px' }}>Click <strong>Apply Template</strong> to fill the week with your regular crews and their times</li>
          <li style={{ marginBottom: '4px' }}>Click any cell or the "+ Add" button to add or remove employees</li>
          <li style={{ marginBottom: '4px' }}>Click on an employee's name to edit their time</li>
          <li style={{ marginBottom: '4px' }}>Employees with time off are shown in red and cannot be added</li>
          <li>Employees scheduled at another location that day are grayed out</li>
        </ul>
      </div>
    </div>
  );
}

export default Scheduling;